import React, { useState } from "react";
import api from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { UserPlus, ArrowRight } from "lucide-react";

/**
 * Manual "Add Client" entry — for prospects met in person, socially, or
 * through family. Under the hood it creates a Prospect with source="manual";
 * if Discovery has already happened the backend fast-tracks to a Client
 * sitting at Goal Mapping & Risk Profiling.
 */
export default function AddClientDialog({ open, onOpenChange, onCreated }) {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(blank());
  const [disc, setDisc] = useState(false);
  const [notes, setNotes] = useState({
    health_insurance: "", term_insurance: "", emergency_fund: "",
    existing_portfolio: "", help_required: "", basic_goals: "", next_steps: "",
  });

  const upd = (k, v) => setForm({ ...form, [k]: v });
  const updNote = (k, v) => setNotes({ ...notes, [k]: v });

  const save = async () => {
    if (!form.full_name.trim()) { toast.error("Full name is required."); return; }
    if (!form.mobile.trim() && !form.email.trim()) {
      toast.error("Enter at least a mobile OR an email."); return;
    }
    setSaving(true);
    try {
      const body = {
        ...form,
        age: form.age === "" ? null : Number(form.age),
        monthly_income: form.monthly_income === "" ? null : Number(form.monthly_income),
        monthly_expenditure: form.monthly_expenditure === "" ? null : Number(form.monthly_expenditure),
        surplus_available_to_invest: form.surplus_available_to_invest === "" ? null : Number(form.surplus_available_to_invest),
        discovery_already_happened: disc,
        discovery_notes: disc ? notes : undefined,
      };
      const { data } = await api.post("/prospects/manual", body);
      toast.success(
        data.converted_client_id
          ? "Client created — jumped straight to Goal Mapping."
          : "Prospect added — ready for a Discovery Call."
      );
      onOpenChange(false);
      setForm(blank());
      setDisc(false);
      onCreated && onCreated(data);
      if (data.converted_client_id) {
        navigate(`/app/clients/${data.converted_client_id}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save.");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="add-client-dialog">
        <DialogHeader>
          <DialogTitle>Add Client</DialogTitle>
          <DialogDescription>
            For prospects met in person, socially, or through family connections. Only
            name + one contact field is required — fill in what you know.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="mb-1 block text-xs font-semibold text-muted-foreground">Full name *</label>
            <Input value={form.full_name} onChange={(e) => upd("full_name", e.target.value)}
                   data-testid="manual-full-name" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-muted-foreground">Mobile *</label>
            <Input value={form.mobile} onChange={(e) => upd("mobile", e.target.value)}
                   data-testid="manual-mobile" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-muted-foreground">Email *</label>
            <Input type="email" value={form.email} onChange={(e) => upd("email", e.target.value)}
                   data-testid="manual-email" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-muted-foreground">Occupation</label>
            <Input value={form.occupation} onChange={(e) => upd("occupation", e.target.value)} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-muted-foreground">Age</label>
            <Input type="number" value={form.age} onChange={(e) => upd("age", e.target.value)} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-muted-foreground">Marital status</label>
            <Input value={form.marital_status} onChange={(e) => upd("marital_status", e.target.value)} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-muted-foreground">Monthly income (₹)</label>
            <Input type="number" value={form.monthly_income} onChange={(e) => upd("monthly_income", e.target.value)} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-muted-foreground">Monthly expenditure (₹)</label>
            <Input type="number" value={form.monthly_expenditure} onChange={(e) => upd("monthly_expenditure", e.target.value)} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-muted-foreground">Monthly surplus (₹)</label>
            <Input type="number" value={form.surplus_available_to_invest}
                   onChange={(e) => upd("surplus_available_to_invest", e.target.value)} />
          </div>
          <div className="col-span-2">
            <label className="mb-1 block text-xs font-semibold text-muted-foreground">Current investments / portfolio summary</label>
            <Textarea rows={2} value={form.current_investments_summary}
                      onChange={(e) => upd("current_investments_summary", e.target.value)} />
          </div>
          <div className="col-span-2">
            <label className="mb-1 block text-xs font-semibold text-muted-foreground">Expectations from us</label>
            <Textarea rows={2} value={form.expectations_from_us}
                      onChange={(e) => upd("expectations_from_us", e.target.value)} />
          </div>
        </div>

        <div className="mt-2 rounded-md border border-dashed border-border bg-muted/30 p-3">
          <label className="flex items-center gap-2 text-sm font-medium">
            <input type="checkbox" checked={disc} onChange={(e) => setDisc(e.target.checked)}
                   data-testid="manual-disc-happened" />
            Has the discovery conversation already happened?
          </label>
          {disc && (
            <div className="mt-3 grid grid-cols-2 gap-3">
              {[
                ["basic_goals", "Basic goals"],
                ["next_steps", "Next steps"],
                ["health_insurance", "Health insurance"],
                ["term_insurance", "Term insurance"],
                ["emergency_fund", "Emergency fund"],
                ["existing_portfolio", "Existing portfolio"],
                ["help_required", "Help required"],
              ].map(([k, label]) => (
                <div key={k} className={k === "basic_goals" || k === "next_steps" ? "col-span-2" : ""}>
                  <label className="mb-1 block text-xs font-semibold text-muted-foreground">{label}</label>
                  <Textarea rows={2} value={notes[k]} onChange={(e) => updNote(k, e.target.value)}
                            data-testid={`manual-notes-${k}`} />
                </div>
              ))}
              <p className="col-span-2 text-[11px] text-muted-foreground">
                On save, the Prospect converts immediately and the Client lands at
                <b> Goal Mapping & Risk Profiling</b> (Discovery Call is skipped).
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="gap-1" data-testid="manual-save">
            {saving ? "Saving…" : (disc ? "Create & convert" : "Add prospect")}
            {!saving && <ArrowRight className="h-3.5 w-3.5" />}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function blank() {
  return {
    full_name: "", mobile: "", email: "",
    occupation: "", age: "", marital_status: "",
    monthly_income: "", monthly_expenditure: "", surplus_available_to_invest: "",
    current_investments_summary: "", expectations_from_us: "",
  };
}

export function AddClientButton({ onCreated }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button onClick={() => setOpen(true)} className="gap-1" data-testid="add-client-btn">
        <UserPlus className="h-4 w-4" /> Add Client
      </Button>
      <AddClientDialog open={open} onOpenChange={setOpen} onCreated={onCreated} />
    </>
  );
}
